// What a live widget shows while its data is on the way, and when a call fails
// (track2-plan.md §6, option A). It reuses the prototype's OWN load prompt — the Action
// Map's "Loading real event data…" / "Could not load … — Retry" (`.event-load-prompt*`
// in styles.css) — so live mode looks like Ben's design, not something new. Only ever
// rendered with ?live=1: the snapshot site never waits, so it never shows these.

import { use, useEffect, useLayoutEffect } from "react";
import { LiveModuleContext } from "./query.js";
import { expectedSteps, moduleLoading, stepLabel, useLoadProgress } from "./load-progress.js";

/**
 * Rendered next to the widget: when it appears after a loading screen, the body gets
 * `.live-revealed` for a moment — the content fades up from mostly-visible and the Stats
 * bars grow in (styles.css). Pure CSS on content that's already there: it never holds the
 * data back, and it's readable and clickable at once.
 */
export function LiveReveal({ mod }: { mod: HTMLElement }) {
  useLayoutEffect(() => {
    const flags = mod as unknown as { _liveWasLoading?: boolean };
    if (!flags._liveWasLoading) return;
    flags._liveWasLoading = false;
    const body = mod.querySelector<HTMLElement>(".module-body");
    if (!body) return;
    body.classList.remove("live-revealed");
    void body.offsetWidth; // restart if it's still running
    body.classList.add("live-revealed");
    const t = window.setTimeout(() => body.classList.remove("live-revealed"), 900);
    return () => window.clearTimeout(t);
  });
  return null;
}

// While loading (Ben, 2026-10-16): a faint, shimmering outline of the module that's coming
// (a skeleton), and underneath what it's doing now + a thin bar, step by step
// (load-progress.ts). The server can't say how long is left, so steps are what we show.

type SkeletonKind = "stats" | "table" | "pitch" | "radar" | "bio" | "lines";

const KIND_OF: Record<string, SkeletonKind> = {
  Appearance: "stats",
  Passing: "stats",
  Attacking: "stats",
  Defending: "stats",
  Goalkeeping: "stats",
  "League Table": "table",
  "Match Results": "table",
  Squad: "table",
  Matches: "table",
  Ranking: "table",
  Filter: "table",
  "Shot Map": "pitch",
  "Pass Map": "pitch",
  "Heat Map": "pitch",
  "Action Map": "pitch",
  Radar: "radar",
  "Bio": "bio",
  Match: "bio",
};

function Skeleton({ kind }: { kind: SkeletonKind }) {
  switch (kind) {
    case "stats":
      return (
        <div className="live-skel-stack">
          <div className="live-skel live-skel-box" />
          <div className="live-skel live-skel-chip" />
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="live-skel-row" key={i}>
              <div className="live-skel live-skel-label" />
              <div className="live-skel live-skel-track" />
              <div className="live-skel live-skel-value" />
            </div>
          ))}
        </div>
      );
    case "table":
      return (
        <div className="live-skel-stack">
          <div className="live-skel live-skel-head" />
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div className="live-skel live-skel-line" key={i} />
          ))}
        </div>
      );
    case "pitch":
      return (
        <div className="live-skel-stack">
          <div className="live-skel-pitch">
            <div className="live-skel-pitch-half" />
            <div className="live-skel-pitch-circle" />
          </div>
        </div>
      );
    case "radar":
      return (
        <div className="live-skel-stack">
          <div className="live-skel-radar">
            <div className="live-skel-radar-ring" />
            <div className="live-skel-radar-ring live-skel-radar-ring-inner" />
          </div>
        </div>
      );
    case "bio":
      return (
        <div className="live-skel-stack">
          <div className="live-skel-row">
            <div className="live-skel live-skel-avatar" />
            <div className="live-skel-stack live-skel-grow">
              <div className="live-skel live-skel-line" />
              <div className="live-skel live-skel-line live-skel-short" />
            </div>
          </div>
          {[0, 1, 2, 3].map((i) => (
            <div className="live-skel live-skel-line" key={i} />
          ))}
        </div>
      );
    default:
      return (
        <div className="live-skel-stack">
          {[0, 1, 2, 3, 4].map((i) => (
            <div className="live-skel live-skel-line" key={i} />
          ))}
        </div>
      );
  }
}

export function LiveLoading() {
  const mod = use(LiveModuleContext);
  const { done, current } = useLoadProgress(mod);
  useEffect(() => {
    if (!mod) return;
    // For the page-wide bar, and so the content fades in when it lands (LiveReveal).
    (mod as unknown as { _liveWasLoading?: boolean })._liveWasLoading = true;
    moduleLoading(mod, true);
    return () => moduleLoading(mod, false);
  }, [mod]);
  const title = mod?.querySelector<HTMLElement>(".module-title")?.innerText ?? "";
  // The player picker's label (a short name), when the module has one.
  const player = mod?.querySelector<HTMLSelectElement>(".player-select")?.selectedOptions[0]?.text || null;
  const total = expectedSteps(title, done);
  const step = Math.min(done + 1, total);
  return (
    <div className="live-loading">
      <Skeleton kind={KIND_OF[title] ?? "lines"} />
      <div className="live-loading-status">
        <div className="live-loading-text">
          <span>{stepLabel(current, player)}…</span>
          <span className="live-loading-step">
            {step} of {total}
          </span>
        </div>
        <div className="live-loading-bar">
          <div className="live-loading-bar-fill" style={{ width: `${Math.round((done / total) * 100)}%` }} />
        </div>
      </div>
    </div>
  );
}

export function LiveError({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="stats-empty event-load-prompt">
      <p className="event-load-prompt-text">Could not load the data — check your connection and try again.</p>
      <button type="button" className="event-load-prompt-btn" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

/** A widget still on the demo snapshot, asked to show a player the snapshot doesn't have
 *  (ids-3, fork 1B): it says so instead of showing someone else. Goes away for each widget
 *  as its own Track 2 slice makes it live. */
export function LiveNotInDemoData({ name }: { name: string }) {
  return (
    <div className="stats-empty event-load-prompt">
      <p className="event-load-prompt-text">
        {name} — no data yet: this widget still uses the demo data.
      </p>
    </div>
  );
}
